import { Id, Ordering } from 'types/definitions'
import { DocumentArchival, DocumentRetention } from './DocumentRetention'
import { DocumentStage, ManualAttestationReason } from './DocumentStage'
import { DocumentLinks } from './DocumentLinks'
import type {
  AdminDocumentsQuery,
  JobDocumentsQuery,
  UploadDocumentRequest,
  UserDocumentsQuery,
} from './DocumentStore'
import {
  CreateDocumentPayload,
  DocumentDTO,
  FileDescriptor,
  FindDocumentsQuery,
  ManualVerificationReason,
  SearchDocumentsFields,
  SearchDocumentsQuery as LegacySearchDocumentsQuery,
} from './dtos/document'
import { DocumentAccessDTO } from './dtos/document-access'
import { match } from 'utils/pattern-match-util'

/**
 * Maps a search documents query to the legacy find documents query.
 */
export function mapSearchQueryToFindDocumentsQuery(
  query: JobDocumentsQuery | UserDocumentsQuery,
): FindDocumentsQuery {
  const base = {
    limit: query.limit,
    after: query.after,
    archived: query.archived,
    workspace_id: query.workspaceId,
    order_by: mapOrderBy(query.orderBy),
  }

  return query.context === 'Job'
    ? { ...base, context: 'Job', job_id: Id.assert(query.jobId) }
    : { ...base, context: 'User' }
}

/**
 * Maps a search documents query to the legacy search documents query.
 */
export function mapSearchQueryToSearchDocumentsQuery(
  query: AdminDocumentsQuery,
): LegacySearchDocumentsQuery {
  return {
    search_text: query.searchText || undefined,
    owner_public_ids: query.ownerPublicIds,
    workspace_ids: query.workspaceIds,
    job_ids: query.jobIds,
    document_type_ids: query.documentTypeIds,
    archived: query.archived,
    limit: query.limit,
    after: query.after,
    order_by: mapOrderBy(query.orderBy),
  }
}

/**
 * Maps the single ordering to the legacy ordering list.
 */
function mapOrderBy(
  orderBy: JobDocumentsQuery['orderBy'],
): Array<Ordering<SearchDocumentsFields>> | undefined {
  return orderBy
    ? [
        {
          field: match(orderBy.field, {
            title: () => SearchDocumentsFields.Title,
            date_created: () => SearchDocumentsFields.DateCreated,
            date_modified: () => SearchDocumentsFields.DateModified,
          }),
          order: orderBy.direction,
        } satisfies Ordering<SearchDocumentsFields>,
      ]
    : undefined
}

/**
 * Maps a v3 document file state to the document stage.
 */
export function mapFileDescriptorToDocumentStage(
  file: FileDescriptor | undefined,
): DocumentStage {
  switch (file?.state) {
    case 'VerifyingClientData':
      return { type: 'AttestationScreening' }
    case 'ManualVerificationRequired':
      return {
        type: 'ManualAttestationRequired',
        reason: mapManualVerificationReasonToManualAttestationReason(
          file.manual_verification_reason,
        ),
      }
    case 'Downloadable':
    case 'Purged':
      return { type: 'Released' }
    case 'Failed':
      return { type: 'Rejected', rejection: 'ConversionFailed' }
    case 'Pending':
    case 'Scanning':
    default:
      return DocumentStage.initial
  }
}

/**
 * Maps a v3 document manual verification reason to the document manual attestation reason.
 */
export function mapManualVerificationReasonToManualAttestationReason(
  reason?: ManualVerificationReason,
): ManualAttestationReason | undefined {
  if (!reason) {
    return undefined
  }

  return match(reason, {
    ClientNameMismatch: () => 'ClientNameMismatch',
    MissingProSignature: () => 'MissingProSignature',
  })
}

/**
 * Maps the accesses of a document resource to the document links.
 */
export function mapAccessesToDocumentLinks(
  accesses: DocumentAccessDTO[] | undefined,
): DocumentLinks {
  if (!accesses) {
    return DocumentLinks.empty
  }

  return {
    job: {
      jobIds: accesses
        .filter((access) => access.type === 'Job')
        .map((access) => access.job_id),
    },
    workRequest: { workRequestId: null },
    strategy: { folderId: null },
  }
}

/**
 * Maps a document resource to the document retention.
 */
export function mapDocumentRetention(
  resource: Partial<DocumentDTO>,
): DocumentRetention {
  const archival = mapDocumentArchival(resource)

  if (resource.latest_version?.purged) {
    // Purge details are not exposed by the API and probably we don't need it for the old API.
    return { type: 'Purged', archival, purge: null }
  }

  if (resource.archived) {
    return { type: 'Archived', archival }
  }

  return DocumentRetention.active
}

/**
 * Maps an upload document request to the legacy create document payload.
 * The title comes from the uploaded file's name.
 */
export function mapUploadRequestToCreateDocumentPayload(
  request: UploadDocumentRequest,
): CreateDocumentPayload {
  return {
    workspace_id: Id.assert(request.workspaceId),
    title: request.file.name,
    document_type_id: request.documentTypeId,
    access: request.jobId
      ? { type: 'Job', job_id: Id.assert(request.jobId) }
      : undefined,
  }
}

/**
 * Maps the archival fields of a document resource to the archival information.
 */
function mapDocumentArchival(
  resource: Partial<DocumentDTO>,
): DocumentArchival | null {
  if (!resource.archived_date || !resource.archived_by_user_public_id) {
    return null
  }

  return {
    archivedAt: new Date(resource.archived_date),
    archivedBy: Id(resource.archived_by_user_public_id),
  }
}
